import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import {
  BufferGeometry,
  DoubleSide,
  Group as ThreeGroup,
  Line as ThreeLine,
  LineBasicMaterial,
  Mesh as ThreeMesh,
  PerspectiveCamera,
  Quaternion,
  Vector3 as ThreeVector3,
} from "three";
import {
  EARTH_AXIAL_TILT_DEGREES,
  LUNAR_ORBIT_INCLINATION_DEGREES,
  MOON_RADIUS,
  SYNODIC_MONTH_DAYS,
} from "../simulation/constants";
import {
  getEarthAxis,
  getMoonPosition,
  getOrbitalNormal,
  phaseIndexToDay,
} from "../simulation/geometry";
import { PHASE_NAMES } from "../simulation/types";
import type { Vector3 } from "../simulation/types";
import { LabelSprite } from "./LabelSprite";
import { phaseMarkerOffset, worldRadiusForPixelDiameter } from "./sceneMath";

const PHASE_MARKER_HIT_DIAMETER_PIXELS = 48;

function PlaneDisk({
  normal,
  radius,
  color,
  opacity,
}: {
  readonly normal: Vector3;
  readonly radius: number;
  readonly color: string;
  readonly opacity: number;
}) {
  const quaternion = useMemo(
    () =>
      new Quaternion().setFromUnitVectors(
        new ThreeVector3(0, 0, 1),
        new ThreeVector3(...normal).normalize(),
      ),
    [normal],
  );
  const edgePoints = useMemo(
    () =>
      Array.from({ length: 97 }, (_, index) => {
        const angle = (index / 96) * Math.PI * 2;
        return new ThreeVector3(
          Math.cos(angle) * radius,
          Math.sin(angle) * radius,
          0,
        );
      }),
    [radius],
  );

  return (
    <group quaternion={quaternion}>
      <mesh renderOrder={-2}>
        <circleGeometry args={[radius, 96]} />
        <meshBasicMaterial
          color={color}
          depthWrite={false}
          opacity={opacity * 0.45}
          side={DoubleSide}
          transparent
        />
      </mesh>
      <LinePath points={edgePoints} color={color} opacity={0.65} />
    </group>
  );
}

function LinePath({
  points,
  color,
  opacity = 1,
}: {
  readonly points: readonly ThreeVector3[];
  readonly color: string;
  readonly opacity?: number;
}) {
  const geometry = useMemo(
    () => new BufferGeometry().setFromPoints([...points]),
    [points],
  );
  const line = useMemo(
    () =>
      new ThreeLine(
        geometry,
        new LineBasicMaterial({ color, opacity, transparent: true }),
      ),
    [color, geometry, opacity],
  );

  useEffect(
    () => () => {
      geometry.dispose();
      line.material.dispose();
    },
    [geometry, line.material],
  );

  return <primitive object={line} />;
}

function getAngleArcPoints(
  from: Vector3,
  to: Vector3,
  radius: number,
  steps = 40,
): ThreeVector3[] {
  const start = new ThreeVector3(...from).normalize();
  const end = new ThreeVector3(...to).normalize();
  const axis = new ThreeVector3().crossVectors(start, end);

  if (axis.lengthSq() <= 1e-12) {
    throw new RangeError("Angle arc vectors must define a plane");
  }

  axis.normalize();
  const angle = start.angleTo(end);
  return Array.from({ length: steps + 1 }, (_, index) =>
    start
      .clone()
      .applyAxisAngle(axis, (angle * index) / steps)
      .multiplyScalar(radius),
  );
}

function PhaseMarker({
  phasePosition,
  isActive,
  selectPhase,
}: {
  readonly phasePosition: Vector3;
  readonly isActive: boolean;
  readonly selectPhase: () => void;
}) {
  const hitTarget = useRef<ThreeMesh>(null);
  const visibleMarker = useRef<ThreeGroup>(null);
  const worldPosition = useMemo(() => new ThreeVector3(), []);
  const offset = useMemo(
    () => phaseMarkerOffset(phasePosition),
    [phasePosition],
  );

  useFrame(({ camera, size }) => {
    visibleMarker.current?.quaternion.copy(camera.quaternion);

    if (hitTarget.current !== null && camera instanceof PerspectiveCamera) {
      hitTarget.current.getWorldPosition(worldPosition);
      const distance = camera.position.distanceTo(worldPosition);
      const radius = worldRadiusForPixelDiameter(
        distance,
        camera.fov,
        size.height,
        PHASE_MARKER_HIT_DIAMETER_PIXELS,
      );
      hitTarget.current.scale.setScalar(radius);
    }
  });

  return (
    <group position={offset}>
      <mesh
        ref={hitTarget}
        scale={0.75}
        onClick={(event) => {
          event.stopPropagation();
          selectPhase();
        }}
      >
        <sphereGeometry args={[1, 12, 8]} />
        <meshBasicMaterial
          colorWrite={false}
          depthWrite={false}
          opacity={0}
          transparent
        />
      </mesh>
      <group ref={visibleMarker}>
        <mesh renderOrder={5} rotation={[0, 0, isActive ? Math.PI / 4 : 0]}>
          {isActive ? (
            <ringGeometry args={[0.18, 0.3, 4]} />
          ) : (
            <ringGeometry args={[0.09, 0.15, 20]} />
          )}
          <meshBasicMaterial
            color={isActive ? "#ffd166" : "#73a9d8"}
            depthTest={false}
            depthWrite={false}
            side={DoubleSide}
          />
        </mesh>
      </group>
    </group>
  );
}

export function OrbitGuides({
  activePhaseIndex,
  showAllPhasePositions,
  showOrbitalPlanes,
  showPhaseLabels,
  selectPhase,
}: {
  readonly activePhaseIndex: number;
  readonly showAllPhasePositions: boolean;
  readonly showOrbitalPlanes: boolean;
  readonly showPhaseLabels: boolean;
  readonly selectPhase: (phaseIndex: number) => void;
}) {
  const earthAxis = useMemo(() => getEarthAxis(), []);
  const orbitalNormal = useMemo(() => getOrbitalNormal(), []);
  const orbitPoints = useMemo(
    () =>
      Array.from(
        { length: 193 },
        (_, index) =>
          new ThreeVector3(
            ...getMoonPosition((index / 192) * SYNODIC_MONTH_DAYS),
          ),
      ),
    [],
  );
  const phasePositions = useMemo(
    () =>
      PHASE_NAMES.map((_, index) => getMoonPosition(phaseIndexToDay(index))),
    [],
  );
  const earthTiltArc = useMemo(
    () => getAngleArcPoints([0, 1, 0], earthAxis, 3.2),
    [earthAxis],
  );
  const lunarInclinationArc = useMemo(
    () => getAngleArcPoints([0, 1, 0], orbitalNormal, 4.1),
    [orbitalNormal],
  );
  const earthTiltLabelPosition = earthTiltArc[20]?.toArray() as Vector3;
  const lunarTiltLabelPosition = lunarInclinationArc[20]?.toArray() as Vector3;

  return (
    <group>
      <LinePath points={orbitPoints} color="#a9b8d0" opacity={0.72} />

      {phasePositions.map((position, index) => {
        const isActive = index === activePhaseIndex;
        const name = PHASE_NAMES[index] ?? `Phase ${index + 1}`;
        return (
          <group key={name} position={position}>
            <PhaseMarker
              isActive={isActive}
              phasePosition={position}
              selectPhase={() => selectPhase(index)}
            />
            {showPhaseLabels ? (
              <LabelSprite
                text={name}
                position={[0, 0.72, 0]}
                scale={[2.5, 0.62, 1]}
                color={isActive ? "#ffd166" : "#dce8f8"}
              />
            ) : null}
            {showAllPhasePositions && !isActive ? (
              // Ghost moon lit by the same sunlight, so each position shows its phase.
              <mesh>
                <sphereGeometry args={[MOON_RADIUS * 0.92, 32, 16]} />
                <meshStandardMaterial
                  color="#cfd9e8"
                  depthWrite={false}
                  opacity={0.45}
                  roughness={0.94}
                  transparent
                />
              </mesh>
            ) : null}
          </group>
        );
      })}

      {showOrbitalPlanes ? (
        <group>
          <PlaneDisk
            normal={[0, 1, 0]}
            radius={9.7}
            color="#4a90c2"
            opacity={0.18}
          />
          <PlaneDisk
            normal={earthAxis}
            radius={4.8}
            color="#52c1a5"
            opacity={0.22}
          />
          <PlaneDisk
            normal={orbitalNormal}
            radius={8.65}
            color="#9c7bd8"
            opacity={0.2}
          />
          <LinePath points={earthTiltArc} color="#ffd166" />
          <LinePath points={lunarInclinationArc} color="#e5a6ff" />
          <LabelSprite text="Ecliptic plane" position={[6.2, 0.18, -5.7]} />
          <LabelSprite
            text="Earth equatorial plane"
            position={[2.8, 0.4, 2.5]}
            scale={[4.8, 1.05, 1]}
            color="#83e6cf"
          />
          <LabelSprite
            text="Moon orbital plane"
            position={[-2.5, 0.5, 6.7]}
            scale={[4.3, 1, 1]}
            color="#d5b8ff"
          />
          <LabelSprite
            text={`Earth axial tilt: ${EARTH_AXIAL_TILT_DEGREES}°`}
            position={[
              earthTiltLabelPosition[0] + 1.6,
              earthTiltLabelPosition[1],
              earthTiltLabelPosition[2] + 0.9,
            ]}
            scale={[5.2, 1.05, 1]}
            color="#ffd166"
          />
          <LabelSprite
            text={`Moon orbit inclination: ${LUNAR_ORBIT_INCLINATION_DEGREES}°`}
            position={[
              lunarTiltLabelPosition[0] - 2,
              lunarTiltLabelPosition[1] + 0.3,
              lunarTiltLabelPosition[2] - 0.8,
            ]}
            scale={[5.7, 1.05, 1]}
            color="#e5a6ff"
          />
        </group>
      ) : null}
    </group>
  );
}
